import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  ArrowUpRight, ArrowDownRight, Receipt, FolderKanban, Megaphone, Calendar,
  RotateCw, XCircle, CreditCard, ShoppingBag,
} from 'lucide-react';
import { toast } from 'sonner';

/** Top-level action bar for any subscriber dashboard — every Zameen-style action one click away. */
export function SubscriptionActionBar() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  const { data: active } = useQuery({
    queryKey: ['action-bar-subscription', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from('subscriptions' as any)
        .select('id, status, auto_renew, ends_at')
        .eq('user_id', user!.id)
        .in('status', ['active', 'past_due'])
        .order('ends_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data as any;
    },
  });

  const canRenew = !!active && active.auto_renew === false && active.status !== 'expired';
  const canCancel = !!active && active.auto_renew === true && active.status === 'active';

  const cancelRenewal = async () => {
    if (!active) return;
    setBusy(true);
    const { error } = await supabase.rpc('cancel_subscription_renewal' as any, { _subscription_id: active.id });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success('Auto-renewal cancelled. Access continues until the period ends.');
    qc.invalidateQueries();
  };

  const resumeRenewal = async () => {
    if (!active) return;
    setBusy(true);
    const { error } = await supabase.rpc('reactivate_subscription_renewal' as any, { _subscription_id: active.id });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success('Auto-renewal re-enabled.');
    qc.invalidateQueries();
  };

  return (
    <div className="flex flex-wrap gap-2">
      <Button asChild size="sm" variant="default" className="gap-1">
        <Link to="/advertise"><ArrowUpRight className="h-3.5 w-3.5" /> Upgrade tier</Link>
      </Button>
      <Button asChild size="sm" variant="outline" className="gap-1">
        <Link to="/advertise"><ArrowDownRight className="h-3.5 w-3.5" /> Downgrade</Link>
      </Button>
      {canRenew && (
        <Button size="sm" variant="outline" className="gap-1" onClick={resumeRenewal} disabled={busy}>
          <RotateCw className="h-3.5 w-3.5" /> Renew / Resume
        </Button>
      )}
      {canCancel && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="outline" className="gap-1 text-destructive hover:text-destructive">
              <XCircle className="h-3.5 w-3.5" /> Cancel renewal
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel auto-renewal?</AlertDialogTitle>
              <AlertDialogDescription>
                Your current period stays active until it expires. Pending renewal invoices will be cancelled.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep auto-renewal</AlertDialogCancel>
              <AlertDialogAction onClick={cancelRenewal}>Yes, cancel renewal</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      <Button asChild size="sm" variant="outline" className="gap-1">
        <Link to="/dashboard/credit-store"><ShoppingBag className="h-3.5 w-3.5" /> Buy credits</Link>
      </Button>
      <Button asChild size="sm" variant="outline" className="gap-1">
        <Link to="/dashboard/profile?section=payment-methods"><CreditCard className="h-3.5 w-3.5" /> Payment methods</Link>
      </Button>
      <Button asChild size="sm" variant="outline" className="gap-1">
        <Link to="/dashboard/featured-projects"><FolderKanban className="h-3.5 w-3.5" /> Promote project</Link>
      </Button>
      <Button asChild size="sm" variant="outline" className="gap-1">
        <Link to="/dashboard/my-banners"><Megaphone className="h-3.5 w-3.5" /> Banners</Link>
      </Button>
      <Button asChild size="sm" variant="outline" className="gap-1">
        <Link to="/dashboard/events"><Calendar className="h-3.5 w-3.5" /> Events</Link>
      </Button>
      <Button asChild size="sm" variant="ghost" className="gap-1">
        <Link to="/dashboard/my-invoices"><Receipt className="h-3.5 w-3.5" /> Invoices</Link>
      </Button>
    </div>
  );
}
